import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLang, SUPPORTED_LANGS } from '../context/LanguageContext';

const ORIGIN = 'https://www.invictaflow.ch';
const OG_LOCALES = { de: 'de_CH', en: 'en_US', fr: 'fr_CH', it: 'it_CH', es: 'es_ES' };
const PATH_SEO = { '/invictacare': 'care', '/invictafit': 'fit', '/invictadance': 'dance', '/invictabeauty': 'beauty', '/invictadental': 'dental', '/invictaauto': 'auto' };
const OG_IMAGES = { care: '/logos/care-og.jpg', fit: '/logos/fit-og.jpg', dance: '/logos/dance-og.jpg', beauty: '/logos/beauty-og.jpg', dental: '/logos/dental-og.jpg', auto: '/logos/auto-og.jpg' };

const setMeta = (selector, value) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute('content', value);
};

const upsertMeta = (attr, key, value) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (value == null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

const upsertLink = (rel, hreflang, href) => {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const Seo = () => {
  const { lang, t } = useLang();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/admin')) return;

    // Keep ?lang= in the URL in sync so every language has its own shareable URL
    const params = new URLSearchParams(window.location.search);
    if (lang === 'de') params.delete('lang');
    else params.set('lang', lang);
    const qs = params.toString();
    window.history.replaceState(null, '', `${path}${qs ? `?${qs}` : ''}`);

    const urlFor = (l) => `${ORIGIN}${path}${l === 'de' ? '' : `?lang=${l}`}`;

    const productId = PATH_SEO[path];
    const title = productId ? t(`seo.products.${productId}.title`) : t('seo.title');
    const description = productId ? t(`seo.products.${productId}.description`) : t('seo.description');

    document.title = title;
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:locale"]', OG_LOCALES[lang]);
    setMeta('meta[property="og:url"]', urlFor(lang));
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);

    // Social-Share-Bild: Produkt-Logo auf Produktseiten
    const ogImage = productId ? `${ORIGIN}${OG_IMAGES[productId]}` : null;
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:image:width', ogImage ? '1200' : null);
    upsertMeta('property', 'og:image:height', ogImage ? '630' : null);
    upsertMeta('property', 'og:image:alt', ogImage ? title : null);
    upsertMeta('name', 'twitter:image', ogImage);
    setMeta('meta[name="twitter:card"]', ogImage ? 'summary_large_image' : 'summary');

    // Products are not translated into Spanish – no es alternate on product pages
    const altLangs = productId ? SUPPORTED_LANGS.filter((l) => l !== 'es') : SUPPORTED_LANGS;
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => {
      const hl = el.getAttribute('hreflang');
      if (hl !== 'x-default' && !altLangs.includes(hl)) el.remove();
    });
    upsertLink('canonical', null, urlFor(lang));
    altLangs.forEach((l) => upsertLink('alternate', l, urlFor(l)));
    upsertLink('alternate', 'x-default', urlFor('de'));
  }, [lang, location.pathname, t]);

  return null;
};

export default Seo;
